<script setup lang="ts">

import {computed, getCurrentInstance, onMounted, PropType, reactive, ref, watch} from "vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import {DataSaver} from "@/views/pages/profile/profile-verification";
import debounce from "debounce";

const props = defineProps({
  model: {
    type: Object as PropType<WebFormModel>,
    required: true
  },
  debounceTime: {
    type: Number,
    default: 500
  },
  saveKey: {
    type: String,
    required: true
  }
})

const model = computed(() => props.model);
const emits = defineEmits(['saved', 'loaded'])

const dataSaver = reactive(new DataSaver(props.saveKey ? props.saveKey : 'profile-update-form'));
const autoSave = debounce(() => {
  const values = {};
  Object.keys(model.value.values).forEach((objKey) => {
    if ((typeof model.value.values[objKey] == 'string') || (typeof model.value.values[objKey] == 'boolean')) {
      values[objKey] = model.value.values[objKey];
    }
  })
  dataSaver.save(values);
  emits('saved');
}, 500);
watch(() => model.value.getValuesHash(), (value) => {
  autoSave();
})

const modalName = computed(() => props.saveKey ? props.saveKey + '-modal' : 'profile-update-form-modal');

const touched = ref(false)
const instance = ref(getCurrentInstance());

watch(() => touched.value, (value) => {
  if (value) {
    const savedData = dataSaver.getData();
    if (savedData) {
      instance.value?.proxy?.$modal.show(modalName.value);
    }
  }
})

const touch = () => {
  if (touched.value) return;
  touched.value = true;
}

onMounted(() => {
  instance.value = getCurrentInstance() as any;
})

const goCancel = () => {
  instance.value?.proxy?.$modal.hide(modalName.value);
}

const goConfirm = () => {
  emits('loaded', dataSaver.getData() || {});
  instance.value?.proxy?.$modal.hide(modalName.value);
}

const clear = () => {
  dataSaver.clear();
}

defineExpose({
  touch,
  clear
})
</script>

<template>
<div>
  <modal :name="modalName" :adaptive="true" width="600px"  height="auto" :clickToClose="false" class="modal--autosaver">
    <div class="modal-inner">
      <div>
        {{$t('Ранее вы начинали заполнять эту форму.')}}
        <br/>
        {{$t('Продолжить заполнение или начать с чистого листа?')}}
      </div>
    </div>
    <div class="modal-footer">
      <div class="buttons">
        <button class="btn btn-default" @click="goCancel">{{$t('Отмена')}}</button>
        <button class="btn btn-success" @click="goConfirm">{{$t('Продолжить')}}</button>
      </div>
    </div>
  </modal>
</div>
</template>

<style scoped lang="less">
.modal--autosaver {
  .modal-inner {
    font-weight: 500;
    font-size: 2.4rem;
    padding: 2.5rem;
  }
}
</style>

<i18n>
{
  "en": {
    "Отмена": "Cancel",
    "Продолжить": "Continue",
    "Ранее вы начинали заполнять эту форму.": "You have previously started filling out this form.",
    "Продолжить заполнение или начать с чистого листа?": "Continue filling out or start from scratch?"
  }
}
</i18n>
